import Handsontable from 'handsontable';
import { findIndex, replace } from 'lodash';
import type { MutableRefObject } from 'react';
import { ICustomListData } from 'pages/ListManagement/types';
import { TPossibleErrors } from './ListTemplates/MLinkage';

export const eanValidator =
    (allowEmpty: boolean = false) =>
    (value: any, cb: (valid: boolean) => void) => {
        const pattern = /^(?=\S)(\d{4,20}|\d{2,}_\w+)$/;
        if (value === null || (allowEmpty && value === '')) {
            cb(true);
        } else {
            cb(value ? pattern.test(value.toString()) : false);
        }
    };

export const PDPUnitValidator = function (list: string[], allowEmpty: boolean = false, filteredUnit: string[] = []) {
    return function (this: Handsontable.CellProperties, value: string, cb: (valid: boolean) => void) {
        const applyFilterTo = ['images', 'videos', 'bullet_points'];
        const row = this.instance.getDataAtRow(this.row);
        if (allowEmpty && value === '') {
            cb(true);
        } else if (filteredUnit.length !== 0 && applyFilterTo.includes(row[2])) cb(filteredUnit.includes(value));
        else cb(list.includes(value));
    };
};

export const stringValidatorWREf = function (
    allowEmpty: boolean = false,
    errorsRef: MutableRefObject<{
        [key: number]: string;
    }>,
    fieldName: string,
) {
    return function (this: Handsontable.CellProperties, value: string, cb: (valid: boolean) => void) {
        if (!value || (allowEmpty && value === '')) {
            errorsRef.current[this.row] = `missing_field.${fieldName}`;
            cb(true);
        } else {
            delete errorsRef.current[this.row];
            cb(true);
        }
    };
};

export const eanLinkageValidator = function (
    { childColumn, parentColumn }: { childColumn: number; parentColumn: number },
    allowEmpty: boolean = false,
    errorsRef: MutableRefObject<{
        [key: number]: TPossibleErrors;
    }>,
) {
    return function (this: Handsontable.CellProperties, value: any, cb: (valid: boolean) => void) {
        if (value === null || (allowEmpty && value === '')) {
            if (errorsRef?.current) delete errorsRef.current[this.row];
            cb(true);
            return;
        }
        let isSameAsParent = false;
        let isChildDuplicate = false;
        let isSameAsChild = false;
        let isRowDuplicate = false;

        if (this && value) {
            const rows = this.instance?.getData();
            if (this.col === 0) {
                isSameAsChild = rows.some((r: string[]) => r[childColumn] === value);
                isRowDuplicate = rows.some(
                    (r: string[], index: number) =>
                        this.row !== index && r[this.col] === value && r[childColumn] === rows[this.row][childColumn],
                );
                if (isSameAsChild || isRowDuplicate) {
                    if (isRowDuplicate) errorsRef.current[this.row] = 'duplicate_row';
                    cb(false);
                    return;
                }
            }
            if (this.col === 1) {
                isSameAsParent = rows.some((r: string[]) => r[parentColumn] === value);
                isChildDuplicate = rows.some(
                    (r: string[], index: number) => this.row !== index && r[this.col] === value,
                );
                if (isSameAsParent || isChildDuplicate) {
                    if (isChildDuplicate) errorsRef.current[this.row] = 'duplicate_child';
                    if (isSameAsParent) errorsRef.current[this.row] = 'same_parent';
                    cb(false);
                    return;
                }
            }
        }
        const pattern = /^(?=\S)(\d{4,20}|\d{2,}_\w+)$/;
        const isValid = value ? pattern.test(value.toString()) : false;

        if (isValid && errorsRef?.current) {
            delete errorsRef.current[this.row];
        }

        cb(isValid);
    };
};

export const listValidator =
    (list: string[], allowEmpty: boolean = false) =>
    (value: string, cb: (valid: boolean) => void) => {
        if (allowEmpty && value === '') {
            cb(true);
        } else cb(list.includes(value));
    };

export const retailerValidator =
    (list: ICustomListData[], allowEmpty: boolean = false) =>
    (value: number, cb: (valid: boolean) => void) => {
        if (allowEmpty && !value) {
            cb(true);
        } else {
            cb(findIndex(list, (o) => o.id === value) !== -1);
        }
    };

const checkForFlat = (input: number): boolean => {
    return input % 1 !== 0;
};

export const numberValidator =
    (
        allowEmpty: boolean = false,
        limit?: {
            min?: number;
            max?: number;
        },
        integerOnly: boolean = false,
    ) =>
    (value: string, cb: (valid: boolean) => void) => {
        const newValue = `${value ?? ''}`.trim();
        if (allowEmpty && newValue === '') {
            cb(true);
        } else if (newValue === '') {
            cb(false);
        } else {
            const intValue = Number(newValue);

            const validPattern = integerOnly ? /^-?\d+$/ : /^-?(?:\d+|\d*\.\d+)$/;

            const isValidNumber = integerOnly
                ? Number.isInteger(intValue)
                : Number.isInteger(intValue) || checkForFlat(intValue);

            if (validPattern.test(newValue) && Number.isFinite(intValue) && isValidNumber) {
                if (limit) {
                    if (typeof limit.max === 'number' && intValue > limit.max) cb(false);
                    else if (typeof limit.min === 'number' && intValue < limit.min) cb(false);
                    else cb(true);
                } else {
                    cb(true);
                }
            } else {
                cb(false);
            }
        }
    };

export const stringValidator =
    (allowEmpty: boolean = false) =>
    (value: string, cb: (valid: boolean) => void) => {
        if (value === null || (!allowEmpty && value === '')) {
            cb(false);
        } else cb(true);
    };

export const stringIdValidator =
    (allowEmpty: boolean = false) =>
    (value: string, cb: (valid: boolean) => void) => {
        if (value === null || (!allowEmpty && value === '')) {
            cb(false);
        } else {
            const regex = /^[\d,]+$/;
            cb(regex.test(value));
        }
    };
